import {
  parseCreativeHashDataRows,
  type ParsedCreativeHashData,
} from "./parse-creative-hash-data"

export interface MyBrandConcept {
  label: string
  isPrimary: boolean
}

export type ParsedMyBrandDrawerData = Omit<ParsedCreativeHashData, "concepts"> & {
  concepts: MyBrandConcept[]
}

const CONCEPT_PREFIX = /^(primary|main|secondary);\s*/i

function toTitle(value: string): string {
  return value.replace(/\b\w/g, (c) => c.toUpperCase())
}

function conceptLabel(title: string): string {
  return toTitle(title.replace(CONCEPT_PREFIX, "").trim())
}

/** Missing Scenario is allowed so lightweight fixtures still parse as concepts. */
function isConceptScenario(row: Record<string, unknown>): boolean {
  const scenario = String(row.Scenario ?? "").trim()
  if (scenario === "") return true
  return scenario.toLowerCase() === "concept"
}

function isPrimaryConceptRow(row: Record<string, unknown>): boolean {
  const subFeature = String(row.Sub_Feature ?? "").trim().toLowerCase()
  const title = String(row.Topic_Title ?? "").trim().toLowerCase()
  return subFeature === "primary" || title.startsWith("primary;")
}

function isNonPrimaryConceptRow(row: Record<string, unknown>): boolean {
  const subFeature = String(row.Sub_Feature ?? "").trim().toLowerCase()
  const title = String(row.Topic_Title ?? "").trim().toLowerCase()
  return (
    subFeature === "main" ||
    subFeature === "secondary" ||
    title.startsWith("main;") ||
    title.startsWith("secondary;")
  )
}

function uniquePrimaryFirst(concepts: MyBrandConcept[]): MyBrandConcept[] {
  const seen = new Map<string, MyBrandConcept>()
  for (const concept of concepts) {
    const key = concept.label.toLowerCase()
    const existing = seen.get(key)
    if (!existing) {
      seen.set(key, concept)
      continue
    }
    if (concept.isPrimary && !existing.isPrimary) {
      seen.set(key, { ...existing, isPrimary: true })
    }
  }

  const deduped = [...seen.values()]
  return [
    ...deduped.filter((concept) => concept.isPrimary),
    ...deduped.filter((concept) => !concept.isPrimary),
  ]
}

function parseMyBrandConcepts(rows: Array<Record<string, unknown>>): MyBrandConcept[] {
  const collected: MyBrandConcept[] = []
  for (const row of rows) {
    if (!isConceptScenario(row)) continue
    const isPrimary = isPrimaryConceptRow(row)
    if (!isPrimary && !isNonPrimaryConceptRow(row)) continue
    const label = conceptLabel(String(row.Topic_Title ?? "").trim())
    if (!label) continue
    collected.push({ label, isPrimary })
  }
  return uniquePrimaryFirst(collected)
}

/** Parse My Brand drawer rows, marking Primary concepts without changing Comp parsing. */
export function parseMyBrandDrawerRows(
  rows: Array<Record<string, unknown>>,
): ParsedMyBrandDrawerData {
  const parsed = parseCreativeHashDataRows(rows)
  return {
    ...parsed,
    concepts: parseMyBrandConcepts(rows),
  }
}
